import { useCallback, useMemo } from "react";
import { notification } from "antd";
import * as R from "ramda";

import * as gtag from "../../../../lib/gtag";
import {
  getCandidateTypes,
  moveCandidatesToProcessConfirm,
  moveCandidatesToRejectedConfirm
} from "../../../../utils/batchActions";
import { pluralsForList } from "../../../../utils/strings";
import {
  CANDIDATE_TYPES,
  BATCH_ACTIONS,
  CANDIDATE_LISTS
} from "../../../../constants/opportunity";
import { listHasItems } from "../../../../utils/arrays";
import {
  canAdvanceCandidateToProcess,
  getCandidateScreeningStatus,
  isRejectedScreeningStatus
} from "../../../../utils/candidates";
import { SELECT_CANDIDATES } from "../../../../store/actions/candidates";
import { SELECT_ALL_STATUS } from "../../../../components/ActionsSnackbar/ActionsSnackbar";

function useListBatchActions({
  opportunity,
  candidates,
  dispatch,
  filteredCandidates,
  list,
  listBatchAction,
  listBatchInvite,
  candidatesBatchShare,
  markCandidatesAsRead,
  multipleSelectedCandidates,
  setShareCandidatesModalVisible,
  setCandidatesTagsModalVisible,
  canMove,
  restrictAdvanceToProcessByScreening,
  t
}) {
  const getMultipleSelectedCandidatesWithStatus = useCallback(
    status =>
      R.filter(
        R.both(
          candidate =>
            getCandidateScreeningStatus(candidate, opportunity) === status,
          candidate =>
            candidate.candidateLikeOpportunity && !candidate.isPreviousMatch
        ),
        multipleSelectedCandidates
      ),
    [multipleSelectedCandidates, opportunity]
  );

  const multipleMovableSelectedCandidates = useMemo(
    () =>
      R.filter(
        candidate =>
          candidate.candidateLikeOpportunity && !candidate.isPreviousMatch,
        multipleSelectedCandidates
      ),
    [multipleSelectedCandidates]
  );

  const multipleExternalSelectedCandidates = useMemo(
    () => getCandidateTypes(multipleSelectedCandidates).externalCandidates,
    [multipleSelectedCandidates]
  );

  const multiplePendingSelectedCandidates = useMemo(
    () => getMultipleSelectedCandidatesWithStatus("pending"),
    [getMultipleSelectedCandidatesWithStatus]
  );

  const multipleRejectedSelectedCandidates = useMemo(
    () =>
      R.filter(candidate => {
        const screeningStatus = getCandidateScreeningStatus(
          candidate,
          opportunity
        );

        return (
          isRejectedScreeningStatus(screeningStatus) &&
          candidate.candidateLikeOpportunity &&
          !candidate.isPreviousMatch
        );
      }, multipleSelectedCandidates),
    [multipleSelectedCandidates, opportunity]
  );

  const multipleIneligibleSelectedCandidates = useMemo(
    () =>
      R.filter(
        candidate => !canAdvanceCandidateToProcess(candidate, opportunity),
        multipleSelectedCandidates
      ),
    [multipleSelectedCandidates, opportunity]
  );

  // TODO FIX i18n interpolations to use the <Trans> Component instead of this hack
  const plurals = useMemo(() => pluralsForList(multipleSelectedCandidates), [
    multipleSelectedCandidates
  ]);

  const pluralsPending = useMemo(
    () => pluralsForList(multiplePendingSelectedCandidates),
    [multiplePendingSelectedCandidates]
  );

  const pluralsRejected = useMemo(
    () => pluralsForList(multipleRejectedSelectedCandidates),
    [multipleRejectedSelectedCandidates]
  );

  const handleListBatchAction = useCallback(
    payload => () =>
      listBatchAction({
        candidates: multipleSelectedCandidates,
        ...payload,
        list
      }),
    [list, listBatchAction, multipleSelectedCandidates]
  );

  const handleListBatchInvite = useCallback(
    payload => () =>
      listBatchInvite({
        candidates: multipleExternalSelectedCandidates,
        ...payload,
        list,
        type: CANDIDATE_TYPES.external.type
      }),
    [list, listBatchInvite, multipleExternalSelectedCandidates]
  );

  const handleListBatchShare = useCallback(
    opportunities =>
      candidatesBatchShare({
        action: BATCH_ACTIONS.list.share,
        opportunities,
        candidates: multipleSelectedCandidates
      }),
    [candidatesBatchShare, multipleSelectedCandidates]
  );

  const read = useMemo(() => {
    const candidates = R.pluck("id", multipleSelectedCandidates);
    const readCandidates = R.pathOr([], ["views"], opportunity);
    const unreadCandidates = R.without(readCandidates, candidates);
    const read = unreadCandidates.length < candidates.length;
    return read;
  }, [multipleSelectedCandidates, opportunity]);

  const snackbarActions = useMemo(
    () => [
      {
        "data-test": "multiple-candidates-tags",
        description: t(`opportunity:candidates.multipleSelection.tags`),
        icon: { type: "tags" },
        onClick: () => {
          gtag.event({
            category: "Bulk",
            action: `${list}.Bulk.tags`
          });
          setCandidatesTagsModalVisible(true);
        },
        enabled: [
          CANDIDATE_LISTS.suggested,
          CANDIDATE_LISTS.selected,
          CANDIDATE_LISTS.rejected
        ].includes(list)
      },
      {
        "data-test": "multiple-candidates-mark-as-read",
        description: t(
          `opportunity:candidates.multipleSelection.${
            read
              ? plurals
                ? "markCandidatesAsUnread"
                : "markCandidateAsUnread"
              : plurals
              ? "markCandidatesAsRead"
              : "markCandidateAsRead"
          }`
        ),
        icon: { type: read ? "eye-invisible" : "eye" },
        onClick: () => {
          gtag.event({
            category: "Bulk",
            action: `${list}.Bulk.markAsRead`
          });
          const candidates = R.pluck("id", multipleSelectedCandidates);
          markCandidatesAsRead({
            id: opportunity.id,
            candidates,
            read: !read
          });
        },
        enabled: [
          CANDIDATE_LISTS.suggested,
          CANDIDATE_LISTS.hunting,
          CANDIDATE_LISTS.selected,
          CANDIDATE_LISTS.rejected
        ].includes(list)
      },
      {
        "data-test": "multiple-candidates-move-to-selected",
        description: t(
          `opportunity:candidates.multipleSelection.${
            plurals ? "moveCandidatesToSelected" : "moveCandidateToSelected"
          }`
        ),
        icon: { type: "check-circle" },
        onClick: () => {
          gtag.event({
            category: "Bulk",
            action: `${list}.Bulk.moveToSelected`
          });
          handleListBatchAction({
            action: BATCH_ACTIONS.list.moveToSelected
          })();
        },
        enabled: R.equals(CANDIDATE_LISTS.hunting, list)
      },
      {
        "data-test": "multiple-candidates-move-to-process",
        description: t(
          `opportunity:candidates.multipleSelection.${
            plurals ? "moveCandidatesToProcess" : "moveCandidateToProcess"
          }`
        ),
        icon: { type: "swap" },
        onClick: () => {
          if (
            restrictAdvanceToProcessByScreening &&
            listHasItems(multipleIneligibleSelectedCandidates)
          ) {
            notification.warning({
              message: t("global:alert"),
              description: t(
                "toasts:candidates.advanceToProcessScreeningBlocked"
              )
            });
            return;
          }

          if (
            R.and(
              R.includes(list, [
                CANDIDATE_LISTS.selected,
                CANDIDATE_LISTS.suggested
              ]),
              R.or(
                listHasItems(multiplePendingSelectedCandidates),
                listHasItems(multipleRejectedSelectedCandidates)
              )
            )
          ) {
            gtag.event({
              category: "Bulk",
              action: `${list}.Bulk.moveToProcess`
            });
            moveCandidatesToProcessConfirm({
              plurals,
              pluralsPending,
              pluralsRejected,
              pendingCount: R.length(multiplePendingSelectedCandidates),
              rejectedCount: R.length(multipleRejectedSelectedCandidates),
              selectedCount: R.length(multipleSelectedCandidates),
              onOk: handleListBatchAction({
                action: BATCH_ACTIONS.list.moveToProcess
              })
            });
          } else
            handleListBatchAction({
              action: BATCH_ACTIONS.list.moveToProcess
            })();
        },
        enabled:
          canMove &&
          R.or(
            R.equals(list, CANDIDATE_LISTS.suggested),
            R.and(
              R.equals(list, CANDIDATE_LISTS.selected),
              R.eqBy(
                R.length,
                multipleSelectedCandidates,
                multipleMovableSelectedCandidates
              )
            )
          )
      },
      {
        "data-test": "multiple-candidates-move-to-rejected",
        description: t(
          "opportunity:candidates.multipleSelection.moveCandidatesToRejected"
        ),
        icon: { type: "minus-circle" },
        onClick: () => {
          moveCandidatesToRejectedConfirm({
            count: R.length(multipleSelectedCandidates),
            plurals,
            onOk: () => {
              gtag.event({
                category: "Bulk",
                action: `${list}.Bulk.moveToRejected`
              });
              listBatchAction({
                action: BATCH_ACTIONS.list.moveToRejected,
                candidates: multipleSelectedCandidates,
                list
              });
            }
          });
        },
        enabled: canMove && R.not(R.equals(CANDIDATE_LISTS.rejected, list))
      },
      {
        "data-test": "multiple-candidates-send-invite",
        description: t(
          "opportunity:candidates.multipleSelection.inviteCandidates"
        ),
        icon: { type: "mail" },
        onClick: () => {
          gtag.event({
            category: "Bulk",
            action: `${list}.Bulk.inviteToWorcket`
          });
          handleListBatchInvite({
            action: BATCH_ACTIONS.list.sendInvite
          })();
        },
        enabled: R.and(
          R.equals(CANDIDATE_LISTS.suggested, list),
          R.eqBy(
            R.length,
            multipleSelectedCandidates,
            multipleExternalSelectedCandidates
          )
        )
      },
      {
        "data-test": "multiple-candidates-send-invite-to-opp",
        description: t(
          "opportunity:candidates.multipleSelection.shareCandidateToOpp"
        ),
        icon: { type: "export" },
        onClick: () => {
          gtag.event({
            category: "Bulk",
            action: `${list}.Bulk.recommend`
          });
          setShareCandidatesModalVisible(true);
        },
        enabled: true
      }
    ],
    [
      t,
      list,
      read,
      plurals,
      canMove,
      multipleSelectedCandidates,
      multipleMovableSelectedCandidates,
      multiplePendingSelectedCandidates,
      multipleIneligibleSelectedCandidates,
      multipleExternalSelectedCandidates,
      setCandidatesTagsModalVisible,
      markCandidatesAsRead,
      opportunity.id,
      handleListBatchAction,
      multipleRejectedSelectedCandidates,
      pluralsPending,
      pluralsRejected,
      listBatchAction,
      handleListBatchInvite,
      setShareCandidatesModalVisible,
      restrictAdvanceToProcessByScreening
    ]
  );

  const handleSelectAllChange = useCallback(
    e =>
      dispatch(
        SELECT_CANDIDATES.run({
          isChecked: e.target.checked,
          candidates: filteredCandidates
        })
      ),
    [dispatch, filteredCandidates]
  );

  const selectAllStatus = useMemo(
    () =>
      multipleSelectedCandidates.length >= 1 &&
      multipleSelectedCandidates.length === candidates.length
        ? SELECT_ALL_STATUS.CHECKED
        : multipleSelectedCandidates.length >= 1
        ? SELECT_ALL_STATUS.INDETERMINATE
        : SELECT_ALL_STATUS.UNCHECKED,
    [candidates.length, multipleSelectedCandidates.length]
  );

  return {
    handleListBatchAction,
    handleListBatchInvite,
    handleListBatchShare,
    handleSelectAllChange,
    plurals,
    selectAllStatus,
    snackbarActions
  };
}

export default useListBatchActions;
